/**
 * @class videoObserver
 * @extends {HTMLElement}
 * @description A class that observes a video element and plays it when it is in the viewport. It should not be used in combination with video controls. Video mute, playsinline and loop is advised.
 * @example
 * <video-observer class="block">
 *   {{ section.settings.video | video_tag: preload: "none", muted: true, autoplay: false, loop: true, playsinline: true, controls: false, image_size: '1440x', class: video_class }}
 * </video-observer>
 */
class videoObserver extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  video = null;

  /** @type {HTMLButtonElement | null} */
  muteButton = null;

  /** @type {IntersectionObserver | null} */
  observer = null;

  /** @type {{ threshold: number }} */
  options = {
    threshold: 0.25,
  };

  connectedCallback() {
    const video = this.querySelector('video');
    const muteButton = this.querySelector('[data-video-observer="mute"]');
    const options = JSON.parse(this.dataset.options || '{}');

    this.options = {
      ...this.options,
      ...options,
    };

    if (!video) return;
    this.video = video;
    this.muteButton = muteButton;
    this.initialize();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.muteButton?.removeEventListener('click', this.toggleMute);
  }

  observerCallback = (entries) => {
    entries.forEach((entry) => {
      const { target: video } = entry;

      if (entry.isIntersecting) {
        video.play().catch((e) => {
          // Handle play promise rejection if needed
          console.log('Video playback failed:', e);
        });
      } else {
        video.pause();
      }
    });
  };

  initialize() {
    if (!this.video) return;

    // Ensure the video is muted and plays inline
    this.video.muted = true;
    this.video.setAttribute('playsinline', '');
    this.updateMuteState();
    this.muteButton?.addEventListener('click', this.toggleMute);

    if (!('IntersectionObserver' in window)) {
      this.video.play();
      return;
    }

    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: this.options.threshold,
    });
    this.observer.observe(this.video);
  }

  toggleMute = () => {
    if (!this.video) return;

    this.video.muted = !this.video.muted;
    this.updateMuteState();
  };

  updateMuteState() {
    if (!this.video) return;

    this.video.toggleAttribute('muted', this.video.muted);
    this.classList.toggle('muted', this.video.muted);
    this.muteButton?.setAttribute('aria-pressed', String(this.video.muted));
  }
}

customElements.define('video-observer', videoObserver);
